// Fitness and fatigue from the last four months to Day 1, form beneath. Solid is measured, dashed
// is the estimate from the plan and the ramp. Drag along it to read any day.
import { useRef, useState } from "preact/hooks";
import type { Fitness } from "../lib/fitness";
import { dateLabel, addDays } from "../lib/dates";

export function FitChart({ f, today }: { f: Fitness; today: string }) {
  const [at, setAt] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  const from = addDays(today, -120);
  const days = f.days.filter((d) => d.d >= from);
  if (days.length < 10) return null;
  const W = 360, H = 170, F = 40, top = 10, mid = H - F - 14;
  const n = days.length - 1;
  const hi = Math.max(10, ...days.map((d) => Math.max(d.ctl, d.atl))) * 1.08;
  const fl = Math.max(15, ...days.map((d) => Math.abs(d.tsb)));
  const x = (i: number) => (i / n) * W;
  const y = (v: number) => top + (1 - v / hi) * (mid - top);
  const yf = (v: number) => H - F / 2 - (v / fl) * (F / 2 - 2);
  const ti = days.findIndex((d) => d.d === today);
  const path = (key: "ctl" | "atl", proj: boolean) => days.map((d, i) => ({ d, i })).filter(({ d, i }) => (proj ? i >= ti : i <= ti)).map(({ d, i }, k) => `${k ? "L" : "M"}${x(i).toFixed(1)} ${y(d[key]).toFixed(1)}`).join("");
  const pick = (e: PointerEvent) => { const r = ref.current!.getBoundingClientRect(); setAt(Math.round(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * n)); };
  const p = at != null ? days[at] : f.day1;
  const months = days.map((d, i) => ({ d, i })).filter(({ d, i }) => d.d.slice(8) === "01" && i < n * 0.86);
  return (
    <div class="fitchart">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Fitness ${Math.round(f.now?.ctl ?? 0)} today, ${Math.round(f.day1?.ctl ?? 0)} projected for Day 1`}
        onPointerDown={(e) => { (e.currentTarget as Element).setPointerCapture(e.pointerId); pick(e); }}
        onPointerMove={(e) => { if ((e.buttons & 1) || e.pointerType === "touch") pick(e); }}
        onPointerUp={() => setTimeout(() => setAt(null), 2000)}>
        {months.map(({ i }) => <line x1={x(i)} x2={x(i)} y1={top} y2={H} class="fc-grid" vector-effect="non-scaling-stroke" />)}
        <line x1="0" x2={W} y1={yf(0)} y2={yf(0)} class="fc-zero" vector-effect="non-scaling-stroke" />
        {days.map((d, i) => i % 2 ? null : <rect x={x(i) - 0.8} width="1.6" y={Math.min(yf(0), yf(d.tsb))} height={Math.abs(yf(d.tsb) - yf(0))} class={`fc-form${d.tsb < 0 ? " neg" : ""}${d.proj ? " proj" : ""}`} />)}
        <path d={path("atl", false)} class="fc-atl" vector-effect="non-scaling-stroke" />
        <path d={path("ctl", false)} class="fc-ctl" vector-effect="non-scaling-stroke" />
        <path d={path("ctl", true)} class="fc-ctl proj" vector-effect="non-scaling-stroke" />
        {ti >= 0 && <line x1={x(ti)} x2={x(ti)} y1={top} y2={H} class="fc-today" vector-effect="non-scaling-stroke" />}
        {at != null && <line x1={x(at)} x2={x(at)} y1={top} y2={H} class="fc-cursor" vector-effect="non-scaling-stroke" />}
      </svg>
      <div class="fc-months">{months.map(({ d, i }) => <span class="label" style={`left:${(i / n) * 100}%`}>{new Date(`${d.d}T12:00:00Z`).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })}</span>)}<span class="label end">Day 1</span></div>
      {p && (
        <div class="readouts three">
          <div><span class="label">{at != null ? dateLabel(p.d) : "Day 1"}{p.proj ? " · est." : ""}</span><span class="stat-n">{Math.round(p.ctl)}<small> fitness</small></span></div>
          <div><span class="label">Fatigue</span><span class="stat-n">{Math.round(p.atl)}</span></div>
          <div><span class="label">Form</span><span class="stat-n">{p.tsb > 0 ? "+" : ""}{Math.round(p.tsb)}</span></div>
        </div>
      )}
    </div>
  );
}
